import React from "react";
import {
  Chart,
  ChartArea,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartXAxis,
  ChartYAxis,
  ChartXAxisItem,
  ChartYAxisItem,
  ChartTooltip,
} from "@progress/kendo-react-charts";

interface DataPoint {
  capacity: number;
  [key: string]: number;
}

// Specific metric interfaces
interface HeadDataPoint extends DataPoint {
  head: number;
}

interface BhpDataPoint extends DataPoint {
  bhp: number;
}

interface EfficiencyDataPoint extends DataPoint {
  efficiency: number;
}

// Units for metrics
interface Units {
  capacity: string;
  head: string;
  bhp: string;
  efficiency: string;
}

// Maximum scales for graph axes
interface MaxScales {
  capacity: number;
  head: number;
  bhp: number;
  efficiency: number;
}

// Target points
interface TargetDataPoint extends DataPoint {
  head: number;
}

// Min/Max flow limits
interface MinMaxFlow {
  gkxMin: number;
  gkxMax: number;
  mainPmpMax: number;
  mainPmpMin: number;
  chargePmp1Max: number;
  chargePmp1Min: number;
  chargePmp2Max: number;
  chargePmp2Min: number;
  chargePmp3Max: number;
  chargePmp3Min: number;
  maxBPDRange: number;
}

// Pump names
interface PumpNames {
  mainPump: string; //
}

// Main interface for pump graph data
export interface PumpGraphData {
  head: HeadDataPoint[];
  bhp: BhpDataPoint[];
  efficiency: EfficiencyDataPoint[];
  units: Units;
  maxScales: MaxScales;
  target: TargetDataPoint[];
  targetV: TargetDataPoint[];
  targetH: TargetDataPoint[];
  minMaxFlow: MinMaxFlow;
  pumpNames: PumpNames;
}

interface CompositeCurvesProps {
  data: PumpGraphData;
}

const CompositeCurves: React.FC<CompositeCurvesProps> = ({ data }) => {
  if (!data) return <div>No data available</div>;
  const maxBPDRange = Number(data?.minMaxFlow?.maxBPDRange);
  const scale = maxBPDRange / 1000;
  let xAxisMax;
  if (scale === 4) {
    xAxisMax = maxBPDRange + 500;
  } else if (Number.isInteger(scale)) {
    xAxisMax = maxBPDRange + 1000;
  } else if (scale < 1) {
    xAxisMax = maxBPDRange + 100;
  } else if (scale < 2) {
    xAxisMax = maxBPDRange + 200;
  } else if (scale <= 4) {
    xAxisMax = maxBPDRange + 500;
  } else {
    xAxisMax = Math.ceil(scale) * 1000;
  }
  const xAxisCrossingValues = [0, xAxisMax, xAxisMax];
  return (
    <Chart>
      <ChartArea background="transparent" />

      {/* Legend */}
      <ChartLegend
        position="bottom"
        labels={{ font: "16px Mulish", color: "#fff" }}
      />

      {/* Series */}
      <ChartSeries>
        <ChartSeriesItem
          name="Head"
          type="scatterLine"
          data={data.head}
          xField="capacity"
          yField="head"
          color="#0000FF"
          yAxis="Head"
          style="smooth"
          markers={{ visible: false }}
          width={4.0}
          tooltip={{ visible: true, color: "FFF", format: "{0} x {1}" }}
        />
        <ChartSeriesItem
          name="Power"
          type="scatterLine"
          data={data.bhp}
          xField="capacity"
          yField="bhp"
          color="#FF0000"
          yAxis="Power"
          style="smooth"
          markers={{ visible: false }}
          width={4.0}
          tooltip={{ visible: true, color: "FFF", format: "{0} x {1}" }}
        />
        <ChartSeriesItem
          name="Efficiency"
          type="scatterLine"
          data={data.efficiency}
          xField="capacity"
          yField="efficiency"
          color="#006633"
          yAxis="Efficiency"
          style="smooth"
          markers={{ visible: false }}
          width={4.0}
          tooltip={{ visible: true, color: "FFF", format: "{0} x {1}" }}
        />

        <ChartSeriesItem
          name="Target"
          type="scatter"
          data={data.target}
          xField="capacity"
          yField="head"
          color="#4d4d4d"
          yAxis="Head"
          markers={{
            visible: true,
            type: "square",
            background: "black",
            size: 10,
          }}
          width={2.0}
          labels={{
            visible: true,
            background: "#bfbfbf",
            border: {
              width: 1,
              color: "black",
            },
            font: "11px calibri",
          }}
          tooltip={{
            visible: true,
            color: "#FFF",
            format: "{0} x {1}",
          }}
          visibleInLegend={false}
        />
        <ChartSeriesItem
          name="TargetH"
          type="scatterLine"
          data={data.targetH}
          xField="capacity"
          yField="head"
          color="#bfbfbf"
          yAxis="Head"
          markers={{ visible: true }}
          width={2.0}
          dashType="dot"
          tooltip={{
            visible: false,
          }}
          visibleInLegend={false}
        />
        <ChartSeriesItem
          name="TargetV"
          type="scatterLine"
          data={data.targetV}
          xField="capacity"
          yField="head"
          color="#bfbfbf"
          yAxis="Head"
          markers={{ visible: true }}
          width={2.0}
          dashType="dot"
          tooltip={{
            visible: false,
          }}
          visibleInLegend={false}
        />
      </ChartSeries>

      {/* X Axis */}
      <ChartXAxis>
        <ChartXAxisItem
          name="Capacity"
          title={{
            text: `Capacity (${data?.units?.capacity})`,
            font: "16px Mulish",
            color: "#fff",
          }}
          color="#fff"
          axisCrossingValue={xAxisCrossingValues}
          majorGridLines={{ width: 0.8, color: "#A0A0A0" }}
          minorGridLines={{ width: 0.3, color: "#A0A0A0", visible: true }}
          crosshair={{
            visible: true,
            width: 1,
            color: "#005F7C",
            tooltip: {
              visible: true,
              format: "#.##",
            },
          }}
          min={0}
          max={xAxisMax}
        />
      </ChartXAxis>
      {/* Y Axis */}
      <ChartYAxis>
        <ChartYAxisItem
          name="Head"
          title={{
            text: `Head (${data?.units?.head})`,
            font: "16px Mulish",
            color: "#0000FF",
          }}
          min={0}
          max={data?.maxScales?.head + 1500}
          color="#0000FF"
          majorGridLines={{ width: 0.8, color: "#A0A0A0" }}
          minorGridLines={{ width: 0.3, color: "#A0A0A0", visible: true }}
          crosshair={{ visible: true, width: 1, color: "#005F7C" }}
        />
        <ChartYAxisItem
          name="Power"
          title={{
            text: `Power (${data?.units?.bhp})`,
            font: "16px Mulish",
            color: "#FF0000",
          }}
          min={0}
          max={data?.maxScales?.bhp + 300}
          color="#FF0000"
          crosshair={{ visible: true, width: 1, color: "#005F7C" }}
        />
        <ChartYAxisItem
          name="Efficiency"
          title={{
            text: `Efficiency (%)`,
            font: "16px Mulish",
            color: "#006633",
          }}
          min={0}
          majorUnit={10}
          max={Math.ceil(data?.maxScales?.efficiency / 10) * 10}
          color="#006633"
          crosshair={{ visible: true, width: 1, color: "#005F7C" }}
        />
      </ChartYAxis>

      {/* Tooltip */}
      <ChartTooltip
        visible={true}
        color="red"
        opacity={1.0}
        format="Flow= #: value.x # , Head= #: value.y #"
      />
    </Chart>
  );
};

export default React.memo(CompositeCurves);
